
import Vue from 'vue';
// @ts-ignore
import publicPortfolioUserQuery from '../PublicPortfolioHero/apollo/PublicPortfolioHero.query';
import { TAB_KEYS } from './constants';
// @ts-ignore
import ConnectionsSuggestionsQuery from './apollo/connectionSuggestions.gql';
// @ts-ignore
import PublicPortfolioPublicConnectionsModalQuery from './apollo/publicPortfolioPublicConnectionsModal.gql';
import EmptyIcon from './assets/empty.vue.svg';
import CreateConnectionsMutation from '~/apollo/mutations/CreateConnections.mutation';
import PublicPortfolioModal from '~/components/partials/public-portfolio/PublicPortfolioModal.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import CheckIcon from '~/assets/svg/input-check.vue.svg';
import type { ConnectionSuggestionInterfaceConnection, ConnectionSuggestionInterfaceEdge } from '~/types/graphql';
import { ConnectionSuggestionType } from '~/types/graphql';
import BravadoInfiniteScrollBase from '~/components/common/BravadoInfiniteScroll/BravadoInfiniteScrollBase.vue';
import WarRoomAvatar from '~/components/partials/war-room/WarRoomAvatar.vue';
import { has } from '~/components/utils/core/object';
import BravadoSpinnerV2 from '~/components/common/BravadoSpinnerV2/BravadoSpinnerV2.vue';
import publicPortfolioContactsSyncMixin from '~/components/partials/public-portfolio/mixins/publicPortfolioContactsSyncMixin';

type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

type PublicPortfolioConnectModalData = {
  selectedTabKey: TabKey;
  tabKeys: typeof TAB_KEYS;
  tabs: Readonly<
    Array<{
      key: TabKey;
      title: string;
    }>
  >;
  connectionSuggestions: ConnectionSuggestionInterfaceConnection;
  skipColleaguesIds: Record<string, boolean>;
  skipTopSuggestionsIds: Record<string, boolean>;
  skipSalesPeopleIds: Record<string, boolean>;
};

export default Vue.extend({
  name: 'PublicPortfolioConnectModal',
  components: {
    EmptyIcon,
    BravadoInfiniteScrollBase,
    CheckIcon,
    PublicPortfolioModal,
    BravadoButtonV3,
    BravadoText,
    WarRoomAvatar,
    BravadoSpinnerV2,
  },
  mixins: [publicPortfolioContactsSyncMixin],
  apollo: {
    connectionSuggestions: {
      query: ConnectionsSuggestionsQuery,
      variables(): { type: `${ConnectionSuggestionType}`; first: number } {
        return {
          type: this.connectionsType,
          first: 10,
        };
      },
    },
  },
  data(): PublicPortfolioConnectModalData {
    return {
      selectedTabKey: TAB_KEYS.topSuggestions,
      tabKeys: TAB_KEYS,
      tabs: Object.freeze([
        { key: TAB_KEYS.topSuggestions, title: 'Top Suggestions' },
        { key: TAB_KEYS.colleagues, title: 'Former Colleagues & Classmates' },
        { key: TAB_KEYS.salespeople, title: 'My Contacts' },
      ]),
      connectionSuggestions: {
        edges: [],
        pageInfo: {
          hasNextPage: false,
          hasPreviousPage: false,
        },
        nodes: [],
        totalCount: 0,
      },
      skipColleaguesIds: {},
      skipSalesPeopleIds: {},
      skipTopSuggestionsIds: {},
    };
  },
  computed: {
    connectionsListClass(): Record<string, boolean> {
      return {
        'public-portfolio-connect-modal__list': true,
        'public-portfolio-connect-modal__list--empty': !this.connectionSuggestions.edges.length,
      };
    },
    connectionsType(): `${ConnectionSuggestionType}` {
      return {
        [TAB_KEYS.salespeople]: ConnectionSuggestionType.Salespeople,
        [TAB_KEYS.colleagues]: ConnectionSuggestionType.Colleagues,
        [TAB_KEYS.topSuggestions]: ConnectionSuggestionType.TopSuggestions,
      }[this.selectedTabKey];
    },
    currentSkipBucket(): Record<string, boolean> {
      if (this.connectionsType === ConnectionSuggestionType.Salespeople) {
        return this.skipSalesPeopleIds;
      } else if (this.connectionsType === ConnectionSuggestionType.Colleagues) {
        return this.skipColleaguesIds;
      } else {
        return this.skipTopSuggestionsIds;
      }
    },
  },
  methods: {
    async showMoreConnections() {
      if (!this.connectionSuggestions) return;

      const { endCursor, hasNextPage } = this.connectionSuggestions.pageInfo;
      if (!hasNextPage) return;

      try {
        await this.$apollo.queries.connectionSuggestions.fetchMore({
          variables: {
            after: endCursor,
          },
          updateQuery: (
            { connectionSuggestions: oldResult },
            { fetchMoreResult: { connectionSuggestions: newResult } },
          ) => {
            return {
              connectionSuggestions: {
                __typename: oldResult.__typename,
                edges: [...oldResult.edges, ...newResult.edges],
                pageInfo: newResult.pageInfo,
              },
            };
          },
        });
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
    avatarExists(connection: ConnectionSuggestionInterfaceEdge): boolean {
      return connection.node ? has(connection.node, 'avatarUrl') : false;
    },
    generateInitials(connection: ConnectionSuggestionInterfaceEdge): string {
      return (
        connection.node?.name
          .split(' ')
          .map(name => name[0])
          .join('') || ''
      );
    },
    handleToggle(id: string): void {
      if (has(this.currentSkipBucket, id)) {
        this.currentSkipBucket[id] = !this.currentSkipBucket[id];
      } else {
        this.$set(this.currentSkipBucket, id, true);
      }
    },
    async invite() {
      this.$analytics.logEvent('public-portfolio__invite-contacts');
      await this.$apollo.mutate({
        mutation: CreateConnectionsMutation,
        variables: {
          input: {
            unselectedColleagueIds: Object.keys(this.skipColleaguesIds),
            unselectedSalespeopleIds: Object.keys(this.skipSalesPeopleIds),
            unselectedTopSuggestionIds: Object.keys(this.skipTopSuggestionsIds),
          },
        },
        refetchQueries: [
          {
            query: PublicPortfolioPublicConnectionsModalQuery,
            variables: { slug: this.$route.params.slug },
          },
          {
            query: publicPortfolioUserQuery,
            variables: { slug: this.$route.params.slug },
          },
        ],
      });

      await this.$router.push(
        `?dynamic_modal=${this.$constants.dynamicModals.PUBLIC_PORTFOLIO_CONNECTIONS_SUCCESS_MODAL}`,
      );
    },
  },
});
